import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";

import { useTour } from "hooks/useTour";
import { requestGetUser, requestUserInfo } from "../services";

import type { UserInfoData } from "../types";

export function useProfilePage() {
  const [loading, setLoading] = useState<boolean>();
  const [editMode, setEditMode] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfoData>();
  const [activeForm, setActiveForm] = useState<"personal" | "registration">(
    "personal"
  );

  const showMobileMode = useMediaQuery({ maxWidth: 769 });
  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  const getInfoData = useCallback(async () => {
    try {
      setLoading(true);
      const responseUserInfo = await requestUserInfo();
      const responseUserId = await requestGetUser();
      setUserInfo({ ...responseUserInfo, ...responseUserId });
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      const erro = err.response;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response?.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  }, []);

  useEffect(() => {
    getInfoData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const birthDate = useMemo(() => {
    if (userInfo && userInfo.birthDate) {
      const dateSplit = userInfo.birthDate.split("-");
      return `${dateSplit[2]}/${dateSplit[1]}/${dateSplit[0]}`;
    }
    return null;
  }, [userInfo]);

  const phoneNumber = useMemo(() => {
    if (userInfo && userInfo.phoneDdd && userInfo.phoneNumber) {
      return `(${userInfo.phoneDdd}) ${userInfo.phoneNumber.slice(
        0,
        5
      )}-${userInfo.phoneNumber.slice(5)}`;
    }
    return null;
  }, [userInfo]);

  const shipToAddress = useMemo(() => {
    const addressToFilter = userInfo?.customerAddresses?.length
      ? userInfo?.customerAddresses
      : [];
    const [addressFilttered] = addressToFilter.filter(
      ({ siteId, addressType }) =>
        siteId === userInfo?.customerSiteId && addressType === "SHIP_TO"
    );
    return (
      `${addressFilttered?.addressLine} ${
        addressFilttered?.addressNumber
          ? `, ${addressFilttered?.addressNumber}`
          : ""
      } ${
        addressFilttered?.complement ? `, ${addressFilttered?.complement}` : ""
      } ${
        addressFilttered?.city
          ? `, ${addressFilttered?.city}-${addressFilttered?.stateCode}`
          : ""
      }` || ""
    );
  }, [userInfo]);

  const shipLetterToAddress = useMemo(() => {
    const addressToFilter = userInfo?.customerAddresses?.length
      ? userInfo?.customerAddresses
      : [];
    const [addressFilttered] = addressToFilter.filter(
      ({ siteId, addressType }) =>
        siteId === userInfo?.customerSiteId && addressType === "SHIP_LETTER_TO"
    );
    return (
      `${addressFilttered?.addressLine} ${
        addressFilttered?.addressNumber
          ? `, ${addressFilttered?.addressNumber}`
          : ""
      } ${
        addressFilttered?.complement ? `, ${addressFilttered?.complement}` : ""
      } ${
        addressFilttered?.city
          ? `, ${addressFilttered?.city}-${addressFilttered?.stateCode}`
          : ""
      }` || ""
    );
  }, [userInfo]);

  useEffect(() => {
    setTourType("meus-dados-novo");
    setSteps(1);
  }, [showTour, setTourType, setSteps]);

  return {
    loading,
    editMode,
    setEditMode,
    userInfo,
    activeForm,
    setActiveForm,
    showMobileMode,
    showTour,
    tourStep,
    tourType,
    birthDate,
    phoneNumber,
    shipToAddress,
    shipLetterToAddress,
  };
}
